import { useState, useEffect, useCallback } from "react";
import { formatPlAmount } from "../utils/formatPlAmount";
import type { LastMonthExpenseEntity } from "../../domain/entities/LastMonthExpenseEntity";
import type { LastMonthExpenseCardProps } from "../components/expense_components/LastMonthExpenseCard";
import {
  getExpenseBootstrapData,
  getExpenseBootstrapError,
  getExpenseBootstrapPromise,
} from "../state/expenseBootstrapStore";
import { useExpenseBootstrap } from "./useExpenseBootstrap";

const BAR_MAX_HEIGHT = 80;

function truncateToDecimals(value: number, decimals: number): number {
  if (!Number.isFinite(value) || decimals < 0) return value;
  const factor = 10 ** decimals;
  return Math.trunc(value * factor) / factor;
}

function formatSignedPercentTruncated(value: number, decimals: number): string {
  const truncated = truncateToDecimals(value, decimals);
  const sign = truncated > 0 ? "+" : "";
  return `${sign}${truncated.toFixed(decimals)}%`;
}

function entityToCardProps(
  entity: LastMonthExpenseEntity,
): Omit<LastMonthExpenseCardProps, "loading"> {
  const statusLabel =
    entity.status === "strong"
      ? "Controlled"
      : entity.status === "low"
        ? "Spike"
        : "Elevated";

  const barExpenses = entity.barMonths.map((m) => m.expense);
  const maxBarExpense = Math.max(...barExpenses, 1);
  const barHeights = barExpenses.map((e) =>
    Math.max(12, (e / maxBarExpense) * BAR_MAX_HEIGHT),
  );
  const barLabels = entity.barMonths.map((m) => m.label);
  const barAmounts = entity.barMonths.map((m) => formatPlAmount(m.expense));

  const momPercent =
    entity.momPercent != null
      ? `${formatSignedPercentTruncated(entity.momPercent, 2)} from previous closed month`
      : "— from previous closed month";

  const revenuePercent =
    entity.revenuePercent != null
      ? `${truncateToDecimals(entity.revenuePercent, 2).toFixed(2)}% of Revenue`
      : "— of Revenue";

  return {
    monthLabel: entity.monthLabel,
    statusLabel,
    expenseAmount: formatPlAmount(entity.expense),
    momPercent,
    revenuePercent,
    barHeights,
    barLabels,
    barAmounts,
  };
}

export function useLastMonthExpense(): LastMonthExpenseCardProps & {
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
} {
  useExpenseBootstrap();

  const [card, setCard] = useState<LastMonthExpenseCardProps>({
    monthLabel: "—",
    statusLabel: "—",
    expenseAmount: "—",
    momPercent: "— from previous closed month",
    revenuePercent: "— of Revenue",
    barHeights: [],
    barLabels: [],
    barAmounts: [],
    loading: false,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const syncFromBootstrap = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      let bootstrapEntity = getExpenseBootstrapData();
      if (!bootstrapEntity) {
        const bootstrapPromise = getExpenseBootstrapPromise();
        if (bootstrapPromise) {
          bootstrapEntity = await bootstrapPromise;
        }
      }

      const bootstrapError = getExpenseBootstrapError();
      if (!bootstrapEntity) {
        throw new Error(bootstrapError ?? "Expense bootstrap data unavailable");
      }

      const section = bootstrapEntity.sections.lastMonth;
      if (section.status !== "ok" || !section.data) {
        throw new Error(section.error ?? "Last month expense unavailable");
      }

      setCard({ ...entityToCardProps(section.data), loading: false });
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Failed to load last month expense";
      setError(message);
      setCard({
        monthLabel: "—",
        statusLabel: "—",
        expenseAmount: "—",
        momPercent: "— from previous closed month",
        revenuePercent: "— of Revenue",
        barHeights: [],
        barLabels: [],
        barAmounts: [],
        loading: false,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void syncFromBootstrap();
  }, [syncFromBootstrap]);

  return {
    ...card,
    loading,
    error,
    refetch: syncFromBootstrap,
  };
}
